/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
  EventType,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";

export const msalConfig = {
  auth: {
    clientId: process.env.Client_ID,
    authority: process.env.authority,
    knownAuthorities: ["beckmancoulterdxs.b2clogin.com"],
    redirectUri: process.env.redirectUri, // array of URIs that are known to be valid
    postLogoutRedirectUri: process.env.redirectUri, // Must match your redirect URI in B2C
    navigateToLoginRequestUrl: true,
    },
  cache: {
    cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};



/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
export const msalInstance = new PublicClientApplication(msalConfig);

// Default to using the first account if no account is active on page load
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

// Optional - This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents();

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event) => {
  const loginRequest = {
    prompt: "login",
    forceRefresh: true
  };

  // Handle successful login
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
    console.log('Login successful for account:', account.username);
  }

  // Handle silent token acquisition failure
  if (
    event.eventType === EventType.ACQUIRE_TOKEN_FAILURE &&
    event.interactionType === InteractionType.Silent
  ) {
    console.log('Silent token acquisition failed, redirecting to login immediately...');
    
    // Clear storage to remove expired tokens/grants
    localStorage.clear();
    sessionStorage.clear();
    console.log('Cache cleared to remove expired grants');
    
    msalInstance.acquireTokenRedirect({ ...loginRequest });
  }

  // Handle silent login failure
  if (
    event.eventType === EventType.LOGIN_FAILURE &&
    event.interactionType === InteractionType.Silent
  ) {
    console.log('Silent login failed, redirecting to login...');
    
    // Clear storage to ensure fresh authentication
    localStorage.clear();
    sessionStorage.clear();
    
    msalInstance.acquireTokenRedirect({ ...loginRequest });
  }
});



